/* Mi Prenda: VariantX linked-product navigation. Load once from theme.liquid. */
(() => {
  if (window.mpVariantXNavigation || window.Shopify?.designMode) return;
  window.mpVariantXNavigation = true;

  const appSelector = '#variant-options-block, #variant-options-embed';
  const cache = new Map();
  let controller;
  let revision = 0;
  const originalInert = new WeakMap();
  const root = doc => doc.querySelector('[data-testid="product-information"]')?.closest('.shopify-section');
  if (!root(document)) return;

  const notice = document.createElement('div');
  notice.className = 'mp-variantx-status';
  notice.setAttribute('role', 'status');
  notice.setAttribute('aria-live', 'polite');
  document.body.append(notice);
  const style = document.createElement('style');
  style.textContent = `
    .mp-variantx-status { position:fixed; bottom:24px; left:50%; transform:translateX(-50%);
      z-index:9999; max-width:calc(100% - 32px); padding:12px 20px; border-radius:16px;
      background:#fff; color:#222; box-shadow:0 8px 32px #0002; font:inherit; text-align:center; }
    .mp-variantx-status:empty { display:none; }
    .mp-variantx-loading { opacity:.65; transition:opacity .15s ease; }
    @media(prefers-reduced-motion:reduce) { .mp-variantx-loading { transition:none; } }
  `;
  document.head.append(style);

  function productURL(href) {
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin && /\/products\/[^/]+\/?$/.test(url.pathname) ? url : null;
    } catch { return null; }
  }

  async function page(url, signal) {
    const key = url.pathname + url.search;
    let html = cache.get(key);
    if (!html) {
      const response = await fetch(url.href, { signal, credentials:'same-origin' });
      if (!response.ok) throw new Error('No se pudo cargar el producto.');
      if (new URL(response.url).pathname !== url.pathname) throw new Error('El producto redirige a otra página.');
      html = await response.text();
      cache.set(key, html);
      if (cache.size > 8) cache.delete(cache.keys().next().value);
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = root(doc);
    if (!section?.querySelector('form[action*="/cart/add"] input[name="id"]')) {
      throw new Error('La página no contiene el formulario del producto.');
    }
    return {doc, section};
  }

  function appLoader(shop) {
    const script = document.createElement('script');
    script.src = 'https://entafix.com/variant-options/api/storefront.js?shop=' + encodeURIComponent(shop) + '&t=' + Date.now();
    script.onload = () => script.remove();
    script.onerror = () => {
      script.remove();
      notice.textContent = 'El producto cambió, pero el selector de colores no pudo cargar. Vuelve a intentarlo.';
    };
    document.head.append(script);
  }

  function metadata(doc) {
    document.title = doc.title;
    for (const selector of ['link[rel="canonical"]', 'meta[name="description"]',
      'meta[property="og:title"]', 'meta[property="og:url"]', 'meta[property="og:image"]',
      'meta[property="og:description"]']) {
      const old = document.querySelector(selector);
      const next = doc.querySelector(selector);
      if (old && next) old.replaceWith(document.importNode(next, true));
    }
  }

  async function navigate(url, fromHistory = false) {
    controller?.abort();
    const request = new AbortController();
    controller = request;
    const token = ++revision;
    const current = root(document);
    if (!current) return;
    const timeout = setTimeout(() => request.abort(), 15000);
    if (!originalInert.has(current)) originalInert.set(current, current.inert);
    const wasInert = originalInert.get(current);
    current.inert = true;
    current.setAttribute('aria-busy', 'true');
    current.classList.add('mp-variantx-loading');
    notice.textContent = 'Cambiando color…';
    try {
      const {doc, section} = await page(url, request.signal);
      if (token !== revision) return;
      const next = document.importNode(section, true);
      const app = doc.querySelector(appSelector);
      if (!app?.dataset.shop) throw new Error('No se encontró el selector VariantX del nuevo producto.');

      // The app embed is outside MainContent in the server response. Place it
      // where the official app places it: immediately before buy-buttons-block.
      next.querySelectorAll(appSelector).forEach(el => el.remove());
      const nextApp = document.importNode(app, true);
      nextApp.id = 'variant-options-embed';
      nextApp.replaceChildren();
      const buttons = next.querySelector('buy-buttons-block, .buy-buttons-block');
      if (!buttons) throw new Error('No se encontró el bloque de compra.');
      buttons.before(nextApp);

      // DOMParser scripts stay inert. Native custom elements reconnect normally.
      // Existing gallery quality code listens to shopify:section:load.
      current.replaceWith(next);
      if (!fromHistory) history.pushState({...history.state, mpVariantX:true}, '', url.href);
      metadata(doc);
      next.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles:true, detail:{sectionId:next.id.replace(/^shopify-section-/, '')}
      }));
      window.Shopify?.PaymentButton?.init?.();
      window.ProductModel?.loadShopifyXR?.();
      appLoader(app.dataset.shop);
      document.dispatchEvent(new CustomEvent('mp:product:changed', {
        detail:{url:url.href, productId:app.dataset.productId, sectionId:next.id}
      }));
      notice.textContent = '';
      const focusSelection = () => {
        const selected = Array.from(nextApp.querySelectorAll('a[href]'))
          .find(a => new URL(a.href).pathname === url.pathname);
        if (!selected) return;
        selected.setAttribute('aria-current', 'page');
        selected?.focus({preventScroll:true});
        selectionObserver.disconnect();
      };
      const selectionObserver = new MutationObserver(focusSelection);
      selectionObserver.observe(nextApp, {childList:true, subtree:true});
      setTimeout(() => selectionObserver.disconnect(), 10000);
    } catch (error) {
      if (token !== revision) return;
      console.warn('[Mi Prenda / VariantX]', error);
      notice.textContent = 'No se pudo cambiar el color. Selecciónalo otra vez para reintentar.';
      // Back/forward must never show a product different from the address bar.
      if (fromHistory) location.replace(url.href);
    } finally {
      clearTimeout(timeout);
      if (token === revision) {
        current.inert = wasInert;
        current.removeAttribute('aria-busy');
        current.classList.remove('mp-variantx-loading');
        originalInert.delete(current);
      }
    }
  }

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link?.closest(appSelector) || !link.closest('[data-testid="product-information"]') || link.target === '_blank' || link.hasAttribute('download')) return;
    const url = productURL(link.href);
    if (!url) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (url.pathname === location.pathname && url.search === location.search) return;
    void navigate(url);
  }, true);

  history.replaceState({...history.state, mpVariantX:true}, '', location.href);
  window.addEventListener('popstate', () => {
    const url = productURL(location.href);
    if (url) void navigate(url, true);
    else location.reload();
  });
})();
